import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, CalendarClock, AlertCircle, Building2, DollarSign, Repeat, CalendarRange } from "lucide-react";
import { LIKELINESS_OPTIONS, LIKELINESS_COLORS, LikelinessIcon, formatCurrency } from "./constants";

const SubscriptionDashboard = ({ dashboard }) => {
  if (!dashboard) return null;

  // Uniform card width — sized to comfortably fit the widest expected content
  // (Revenue Collected up to ~$X,XXX,XXX.XX). Cards lay out left-to-right and
  // wrap, so adding MRR/ARR cards just extends the row or wraps naturally.
  const cardWidth = "w-52";

  // Always renders a dollar amount (including "$0.00") — distinct from the
  // shared formatCurrency() which returns "—" for falsy values.
  const formatCurrencyAlways = (amount) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);

  return (
    <>
      <div className="flex flex-wrap gap-3 mb-8">
        <Card className={`bg-white ${cardWidth}`}>
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <Building2 className="w-4 h-4" />Total Subscriptions
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-2xl font-bold text-slate-900 text-center">{dashboard.total_subscriptions}</p>
          </CardContent>
        </Card>
        <Card className={`bg-white border-emerald-200 ${cardWidth}`}>
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm font-medium text-emerald-600 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />Revenue Collected
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-2xl font-bold text-emerald-600 text-center">{formatCurrency(dashboard.total_collected || 0)}</p>
          </CardContent>
        </Card>
        <Card className={`bg-white ${cardWidth}`}>
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />Expected Revenue
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-2xl font-bold text-indigo-600 text-center">{formatCurrency(dashboard.total_expected_revenue)}</p>
          </CardContent>
        </Card>
        <Card className={`bg-white ${cardWidth}`}>
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <CalendarClock className="w-4 h-4" />Renewals This Month
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">{formatCurrencyAlways(dashboard.renewals_this_month_value)}</p>
              <p className="text-2xl font-bold text-slate-900">{dashboard.renewals_this_month}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`bg-white border-red-200 ${cardWidth}`}>
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm font-medium text-red-500 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />Overdue Renewals
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-red-500">{formatCurrencyAlways(dashboard.overdue_value)}</p>
              <p className="text-2xl font-bold text-red-600">{dashboard.overdue_count}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`bg-white ${cardWidth}`}>
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <Repeat className="w-4 h-4" />MRR
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-2xl font-bold text-slate-900 text-center">{formatCurrency(dashboard.total_mrr || 0)}</p>
          </CardContent>
        </Card>
        <Card className={`bg-white ${cardWidth}`}>
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <CalendarRange className="w-4 h-4" />ARR
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-2xl font-bold text-slate-900 text-center">{formatCurrency(dashboard.total_arr || 0)}</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-3 mb-8">
        {LIKELINESS_OPTIONS.map(lh => (
          <Card key={lh} className={`bg-white ${cardWidth}`}>
            <CardContent className="pt-4 px-4 pb-4">
              <div className="flex items-center gap-3">
                <Badge className={LIKELINESS_COLORS[lh]}>
                  <LikelinessIcon type={lh} />
                  <span className="ml-1">{lh}</span>
                </Badge>
                <span className="text-xl font-bold">{dashboard.likeliness_breakdown[lh] || 0}</span>
              </div>
              {lh !== "CANCELLED" && lh !== "FREE" && (
                <p className="text-xs text-slate-500 mt-2">{formatCurrencyAlways(dashboard.likeliness_revenue[lh])}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
};

export default SubscriptionDashboard;
